import { useEffect, useMemo } from "react";
import SessionCardView from "../card-view/SessionCardView";
import BusinessAnalytics from "../business-analytics";
import GrowYourNetwork from "../grow-your-network";
import { useUser } from "contexts/UserContext";
import { Row, Col, Button } from "react-bootstrap";
import { Link, useNavigate } from "react-router-dom";
import OrchidProAvatar from "components/orchid-pro-avatar";
import FullWidthImage from "components/full-width-image";
import ExtraLargeButton from "components/extra-large-button";
import SetupWorkflow from "./components/setup-workflow/SetupWorkflow";
import SetupSubscription from "./components/setup-subscription";
// import AspectRatio from "components/aspect-ratio";
import { useOrchidPro } from "../contexts/orchid-pro-context";
import { useAppointmentsInDateRange } from "contexts/appointments";
import getTodayDate from "utils/getTodayDate";
import {
  getOrchidProCertificationString,
  getOrchidProTypeStringFromNewArr,
  getOrchidProAbbrevNameAndAllDegreesString,
} from "utils/formatter/orchidPro";

import { ReactComponent as OutlineArrowRightCircle } from "assets/svg/outline-arrow-right-circle.svg";
import { ReactComponent as CalendarIcon } from "assets/svg/calendar-icon.svg";
import { ReactComponent as DotDivider } from "assets/svg/dot-divider.svg";
import getClasses from "utils/getClasses";
import styles from "./DashboardHome.module.scss";
import { useSubscription } from "contexts/subscription-context";
import PendingReviewNonAMHC from "components/pending-review-non-amhc";
import { AmplitudePageName, track } from "utils/amplitude";
import { useTimeOnPageContext } from "contexts/time-on-page";
import PlanCard from "components/Subscription/pricing-page/plan-cards/PlanCard";

const ORCHID_PRO_DASHBOARD_LANDING = "/img/dashboard/dashboard-landing.png";
const ANALYTICS_PAGE_NAME: AmplitudePageName = "pro-dashboard-home";

const DashboardHome = () => {
  const { user } = useUser();
  const { orchidPro } = useOrchidPro();
  const navigate = useNavigate();

  const { setPage } = useTimeOnPageContext();
  useEffect(() => {
    setPage(ANALYTICS_PAGE_NAME);
  }, [setPage]);

  const { appointmentsInDateRange: appointments } =
    useAppointmentsInDateRange({
      newestTimestamp: undefined,
      oldestTimestamp: new Date(getTodayDate()).getTime(),
      reloadTimestamp: 0,
    }) || [];

  const { isSubscribed, subscription, planType } = useSubscription();

  useEffect(() => {
    if (planType === "ai-only") {
      navigate("/pro-dashboard/home");
    }
  }, [planType, navigate]);

  // provider needs to subscribe to see setup workflow
  const showSetupWorkflow = useMemo(
    () =>
      isSubscribed &&
      planType &&
      planType !== "ai-only" &&
      user?.profile_approved_to_use_the_platform &&
      !user?.complete_scheduler_tutorial &&
      !user?.has_stripe_account_connected,
    [
      isSubscribed,
      user?.profile_approved_to_use_the_platform,
      user?.complete_scheduler_tutorial,
      user?.has_stripe_account_connected,
      planType,
    ],
  );

  return !isSubscribed ? (
    !subscription ? (
      <SetupSubscription />
    ) : (
      // When existing user's plan has ended
      <div className={styles["dashboard"]}>
        <Row className={styles["dashboard__info"]}>
          <Col md={6}>
            <h3 className="md-18 deep-blue">Subscription</h3>
            <PlanCard
              withInfo={false}
              withRestart
              subPeriod={"annual"}
              amplitudePageName={ANALYTICS_PAGE_NAME}
              subClass="core"
            />
          </Col>
          <Col
            sm={6}
            className="d-flex flex-column align-items-center justify-content-end"
          >
            <h3 className="md-18 deep-blue">Want to see more?</h3>
            <Button
              className={getClasses(
                styles["dashboard__info-button"],
                styles["margin-bottom-6"],
              )}
              as="a"
              href="/pricing"
              onClick={() =>
                track(ANALYTICS_PAGE_NAME, "pricing-link", "click")
              }
            >
              Go to pricing page
            </Button>
          </Col>
        </Row>
      </div>
    )
  ) : !user?.profile_approved_to_use_the_platform ? (
    <PendingReviewNonAMHC />
  ) : (
    <div className={styles["dashboard"]}>
      {showSetupWorkflow && (
        <SetupWorkflow amplitudePageName={ANALYTICS_PAGE_NAME} />
      )}
      <Row className={styles["landing-section"]}>
        <Col md={5} className="p-0">
          <h2 className="rg-32 questrial">
            {user && user.signup_form_finished
              ? `Hi, ${user.first_name} ${user.last_name}!`
              : "Hi!"}
          </h2>
          <div>
            Welcome to Orchid! We can't wait for you to embark on your mental
            health journey with us!
          </div>
        </Col>
        <Col md={7}>
          <FullWidthImage src={ORCHID_PRO_DASHBOARD_LANDING} fluid />
        </Col>
      </Row>

      <Row className={styles["business-analytics-section"]}>
        <BusinessAnalytics analyticsPageName={ANALYTICS_PAGE_NAME} />
      </Row>
      <div className="d-flex justify-content-center">
        <DotDivider />
      </div>
      <Row className={styles["middle-section"]}>
        <Col className={styles["middle-section__left"]}>
          {/* <AspectRatio
            ratio={0.84}
            wrapperClassName={styles["middle-section__left-wrapper"]}
            contentClassName={styles["middle-section__left-content"]}
          > */}
          <div
            className={getClasses(
              "h-100",
              styles["middle-section__left-wrapper"],
              styles["middle-section__left-content"],
            )}
          >
            <div className="d-flex justify-content-between">
              <div className="bd-18">My profile</div>
              <Link
                to="/pro-dashboard/settings/my-profile"
                onClick={() =>
                  track(ANALYTICS_PAGE_NAME, "edit-profile-link", "click")
                }
              >
                <div className="d-flex align-items-center">
                  <div className="mr-2 md-18 secondary-orange">Edit</div>
                  <OutlineArrowRightCircle color={`var(--secondary-orange)`} />
                </div>
              </Link>
            </div>
            <div className="rg-14">
              Customize how your profile looks on Orchid!
            </div>

            {orchidPro && (
              <div className={styles["middle-section__left-profile"]}>
                <div className="text-center">
                  <OrchidProAvatar
                    orchidPro={orchidPro}
                    style={{
                      width: "3.75rem",
                      height: "3.75rem",
                    }}
                    className="d-inline-flex"
                  />

                  <div className="mt-3">
                    <div className={"secondary-orange bd-16"}>
                      {getOrchidProAbbrevNameAndAllDegreesString(orchidPro)}
                    </div>
                    {!orchidPro.orchid_pro_types?.length ? null : (
                      <div className={"bd-14 mb-1"}>
                        {getOrchidProTypeStringFromNewArr(orchidPro)}
                      </div>
                    )}
                    {!orchidPro.orchid_pro_types?.length ? null : (
                      <div className={"rg-12"}>
                        {getOrchidProCertificationString(orchidPro)}
                      </div>
                    )}
                  </div>
                </div>
              </div>
            )}
          </div>
          {/* </AspectRatio> */}
        </Col>
        <Col className={styles["middle-section__right"]}>
          {!appointments.length ? (
            <>
              <div className="mb-2 bd-18 deep-blue">Set up your calendar</div>
              <CalendarIcon className={styles["calendar-icon"]} />
              <div>
                Set up your calendar with your availability or create a group
                session so patients can book you straight through Orchid!
              </div>
              <div className={"d-flex mt-4"}>
                <Link
                  to={`/pro-dashboard/group-sessions`}
                  onClick={() =>
                    track(
                      ANALYTICS_PAGE_NAME,
                      "create-group-session-button",
                      "click",
                    )
                  }
                >
                  <ExtraLargeButton variant="outline-primary">
                    Create a group session
                  </ExtraLargeButton>
                </Link>
                <Link
                  to={`/pro-dashboard/schedule`}
                  onClick={() =>
                    track(
                      ANALYTICS_PAGE_NAME,
                      "setup-1-to-1-availability-button",
                      "click",
                    )
                  }
                  className="ml-3"
                >
                  <ExtraLargeButton variant="primary">
                    Set up 1:1 availability
                  </ExtraLargeButton>
                </Link>
              </div>
            </>
          ) : (
            <>
              <div className="d-flex align-items-start justify-content-between">
                <div>
                  <div className="mb-2 bd-18 deep-blue">Upcoming sessions!</div>
                  <div className="rg-14 gray01">
                    Here's a quick overview of what's on your calendar!
                  </div>
                </div>
                <Link
                  to="/pro-dashboard/schedule"
                  onClick={() => {
                    track(
                      ANALYTICS_PAGE_NAME,
                      "view-my-calendar-link",
                      "click",
                    );
                  }}
                >
                  <div className="d-flex align-items-center md-18">
                    View my Calendar
                    <OutlineArrowRightCircle className="ml-2" />
                  </div>
                </Link>
              </div>
              <SessionCardView
                futureSessions={appointments}
                analyticsPageName={ANALYTICS_PAGE_NAME}
              />
            </>
          )}
        </Col>
      </Row>
      <div className={styles["bottom-section"]}>
        <GrowYourNetwork amplitudePageName={ANALYTICS_PAGE_NAME} />
      </div>
    </div>
  );
};

export default DashboardHome;
